import React, { useEffect, useMemo, useState } from 'react'

import iconSearch from '../../../../../../Assets/Icon/AdminKycQueue/mna0yq0f-rbvml9l.svg'
import iconFilter from '../../../../../../Assets/Icon/AdminInvestors/mna1u69m-8naty3z.svg'
import iconExport from '../../../../../../Assets/Icon/AdminInvestors/mna1u69m-3g3xrs3.svg'
import iconPrev from '../../../../../../Assets/Images/AdminInvestors/mna1u63v-h50m4oa.png'
import iconNext from '../../../../../../Assets/Icon/AdminInvestors/mna1u63v-bt5ccwl.svg'

import financialTransactionsService, {
  type FinancialTransaction,
  type SortOrder,
} from 'Services/financialTransactionsService'
import { ModalWrapper } from 'Components'
import { TimeConverter } from 'Helpers/TimeConverter'

type Props = {
  customerAccountId: string
  customerMsisdn?: string
}

const formatAmount = (currency: string | undefined, amountMajor: number | undefined | null) => {
  if (amountMajor === undefined || amountMajor === null) return '-'
  if (currency) return `${currency} ${amountMajor.toLocaleString()}`
  return amountMajor.toLocaleString()
}

const TransactionsList: React.FC<Props> = ({ customerAccountId, customerMsisdn }) => {
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [rows, setRows] = useState<FinancialTransaction[]>([])
  const [preview, setPreview] = useState<FinancialTransaction | null>(null)

  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(20)
  const [sortOrder, setSortOrder] = useState<SortOrder>('ASC')
  const [paginationData, setPaginationData] = useState<{
    pageNumber: number
    pageSize: number
    total: number
  }>({
    pageNumber: 1,
    pageSize: 20,
    total: 0,
  })

  const getErrorMessage = (e: unknown) => {
    if (typeof e === 'object' && e !== null && 'message' in e) {
      const message = (e as { message?: unknown }).message
      if (typeof message === 'string') return message
      if (message != null) return String(message)
    }
    return null
  }

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true)
      setErrorMessage(null)
      try {
        const response = await financialTransactionsService.list({
          pageNumber,
          pageSize,
          customerAccountId,
          customerMsisdn,
          sortOrder,
        })
        const data = response?.data?.data
        setRows((data?.dataset ?? []) as FinancialTransaction[])
        setPaginationData({
          pageNumber: Number(data?.pageNumber ?? pageNumber),
          pageSize: Number(data?.pageSize ?? pageSize),
          total: Number(data?.total ?? 0),
        })
      } catch (e) {
        setRows([])
        setPaginationData({ pageNumber, pageSize, total: 0 })
        setErrorMessage(getErrorMessage(e) ?? 'Failed to load transactions')
      } finally {
        setLoading(false)
      }
    }

    fetchTransactions()
  }, [customerAccountId, customerMsisdn, pageNumber, pageSize, sortOrder])

  const totalPages = Math.max(1, Math.ceil(paginationData.total / paginationData.pageSize))

  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return rows
    return rows.filter((r) => {
      const haystack = [
        r.id,
        r.reference,
        r.type,
        r.flowType,
        r.paymentProcessor,
        r.paidStatus,
        r.description,
        r.metadata?.transactionNote,
        r.metadata?.orderId,
        r.metadata?.investorTokenAssetId,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(needle)
    })
  }, [q, rows])

  return (
    <>
      <div className='admin-investor__toolbar'>
        <div className='admin-investor-search'>
          <img src={iconSearch} alt='' className='admin-investor-search__icon' />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className='admin-investor-search__input'
            placeholder='Search transactions...'
          />
        </div>

        <button
          type='button'
          className='admin-investor-button'
          onClick={() => {
            setPageNumber(1)
            setSortOrder((s) => (s === 'ASC' ? 'DESC' : 'ASC'))
          }}
        >
          <img src={iconFilter} alt='' className='admin-investor-button__icon' />
          <span>Sort: {sortOrder}</span>
        </button>

        <button type='button' className='admin-investor-button'>
          <img src={iconExport} alt='' className='admin-investor-button__icon' />
          <span>Export</span>
        </button>
      </div>

      <div className='admin-investor-table'>
        <div className='admin-investor-table__head'>
          <div>Reference</div>
          <div>Type</div>
          <div>Amount</div>
          <div>Date</div>
          <div>Status</div>
          <div className='text-end'>Action</div>
        </div>
        <div className='admin-investor-table__body'>
          {loading && (
            <div className='admin-investor-table__row'>
              <div className='admin-investor-table__cell'>Loading...</div>
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
            </div>
          )}

          {!loading && errorMessage && (
            <div className='admin-investor-table__row'>
              <div className='admin-investor-table__cell'>{errorMessage}</div>
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
            </div>
          )}

          {!loading && !errorMessage && filteredRows.length === 0 && (
            <div className='admin-investor-table__row'>
              <div className='admin-investor-table__cell'>No transactions found</div>
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
            </div>
          )}

          {!loading &&
            !errorMessage &&
            filteredRows.map((r) => (
              <div key={r.id} className='admin-investor-table__row'>
                <div className='admin-investor-table__cell'>
                  {r.reference ? r.reference.substring(0, 15) : r.id.substring(0, 15)}...
                </div>
                <div className='admin-investor-table__cell'>{r.type ?? '-'}</div>
                <div className='admin-investor-table__cell'>
                  {formatAmount(r.currency, r.amountMajor)}
                </div>
                <div className='admin-investor-table__cell'>{TimeConverter(r.createdAt)}</div>
                <div className='admin-investor-table__cell'>
                  <span className='admin-investor-table__status admin-investor-table__status--closed'>
                    {r.paidStatus ?? r.flowType ?? '-'}
                  </span>
                </div>
                <div className='admin-investor-table__cell text-end'>
                  <button
                    type='button'
                    className='admin-investor-table__action'
                    onClick={() => setPreview(r)}
                  >
                    View
                  </button>
                </div>
              </div>
            ))}
        </div>

        <div className='admin-investors-pagination'>
          <button
            type='button'
            className='admin-investors-pagination__button'
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            disabled={pageNumber <= 1 || loading}
          >
            <img src={iconPrev} alt='' className='admin-investors-pagination__icon' />
            <span>Previous</span>
          </button>
          <div className='admin-investors-pagination__numbers'>
            Page {pageNumber} of {totalPages}
          </div>
          <button
            type='button'
            className='admin-investors-pagination__button'
            onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
            disabled={pageNumber >= totalPages || loading}
          >
            <span>Next</span>
            <img src={iconNext} alt='' className='admin-investors-pagination__icon' />
          </button>
        </div>
      </div>

      <ModalWrapper
        showModal={Boolean(preview)}
        closeModal={() => setPreview(null)}
        title='Transaction Details'
        size='lg'
      >
        {preview ? (
          <div>
            <div className='admin-kyc-info'>
              <div className='admin-kyc-info__row'>
                <div className='admin-kyc-info__label'>Transaction ID</div>
                <div className='admin-kyc-info__value'>{preview.id}</div>
              </div>
              <div className='admin-kyc-info__row'>
                <div className='admin-kyc-info__label'>Type</div>
                <div className='admin-kyc-info__value'>{preview.type ?? '-'}</div>
              </div>
              <div className='admin-kyc-info__row'>
                <div className='admin-kyc-info__label'>Amount</div>
                <div className='admin-kyc-info__value'>
                  {formatAmount(preview.currency, preview.amountMajor)}
                </div>
              </div>
              <div className='admin-kyc-info__row'>
                <div className='admin-kyc-info__label'>Flow</div>
                <div className='admin-kyc-info__value'>{preview.flowType ?? '-'}</div>
              </div>
              <div className='admin-kyc-info__row'>
                <div className='admin-kyc-info__label'>Reference</div>
                <div className='admin-kyc-info__value'>{preview.reference ?? '-'}</div>
              </div>
              <div className='admin-kyc-info__row'>
                <div className='admin-kyc-info__label'>Paid Status</div>
                <div className='admin-kyc-info__value'>{preview.paidStatus ?? '-'}</div>
              </div>
              <div className='admin-kyc-info__row'>
                <div className='admin-kyc-info__label'>Created At</div>
                <div className='admin-kyc-info__value'>{TimeConverter(preview.createdAt)}</div>
              </div>
              <div className='admin-kyc-info__row'>
                <div className='admin-kyc-info__label'>Description</div>
                <div className='admin-kyc-info__value'>{preview.description ?? '-'}</div>
              </div>
            </div>

            <div className='mt-3'>
              <div className='admin-kyc-card__title'>Metadata</div>
              <div className='admin-kyc-info'>
                <div className='admin-kyc-info__row'>
                  <div className='admin-kyc-info__label'>Order ID</div>
                  <div className='admin-kyc-info__value'>{preview.metadata?.orderId ?? '-'}</div>
                </div>
                <div className='admin-kyc-info__row'>
                  <div className='admin-kyc-info__label'>Transaction Note</div>
                  <div className='admin-kyc-info__value'>
                    {preview.metadata?.transactionNote ?? '-'}
                  </div>
                </div>
                <div className='admin-kyc-info__row'>
                  <div className='admin-kyc-info__label'>Investor Token Asset ID</div>
                  <div className='admin-kyc-info__value'>
                    {preview.metadata?.investorTokenAssetId ?? '-'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </ModalWrapper>
    </>
  )
}

export default TransactionsList
